import { z } from "zod";
import { ClaudeModel } from "./agents.ts";

/** One step of a workflow: a provider account, what to ask it ({{previous}}: the step before's answer, {{input}}: what the run was started with), and whether to wait for the owner's go-ahead first. */
export const WorkflowStep = z.object({
  accountId: z.string().min(1),
  /** the model this step runs on ("default": the provider's own default) */
  model: ClaudeModel.default("default"),
  prompt: z.string().trim().min(1).max(20_000),
  approve: z.boolean().default(false),
});
export type WorkflowStep = z.infer<typeof WorkflowStep>;

export const UpsertWorkflowInput = z.object({
  name: z.string().trim().min(1).max(80),
  spaceId: z.string().nullable().default(null),
  steps: z.array(WorkflowStep).min(1).max(12),
  /** a cron expression to run it on its own (null: only by hand) */
  cron: z.string().trim().max(120).nullable().default(null),
  timezone: z.string().max(64).default("UTC"),
  active: z.boolean().default(true),
});
export type UpsertWorkflowInput = z.infer<typeof UpsertWorkflowInput>;

export const RunWorkflowInput = z.object({ input: z.string().max(20_000).default("") });

export type WorkflowRunStatus = "running" | "waiting" | "done" | "error" | "cancelled";

export interface WorkflowRun {
  id: string;
  workflowId: string;
  workflowName: string;
  chatId: string;
  status: WorkflowRunStatus;
  /** the step running now, or waiting for approval (0-based) */
  step: number;
  total: number;
  input: string;
  error: string | null;
  trigger: "manual" | "schedule";
  startedAt: string;
  finishedAt: string | null;
}

export interface Workflow {
  id: string;
  name: string;
  spaceId: string | null;
  steps: WorkflowStep[];
  cron: string | null;
  timezone: string;
  active: boolean;
  nextRunAt: string | null;
  lastRun: WorkflowRun | null;
  createdAt: string;
  updatedAt: string;
}

/** Fills a step's prompt; the step before's answer is added at the end when the prompt does not place it itself. */
export function workflowPrompt(prompt: string, previous: string | null, input: string): string {
  const has = /\{\{\s*previous\s*\}\}/i.test(prompt);
  let text = prompt.replace(/\{\{\s*input\s*\}\}/gi, () => input).replace(/\{\{\s*previous\s*\}\}/gi, () => previous ?? "");
  if (previous !== null && !has) text += `\n\n---\nThe previous step's result:\n\n${previous}`;
  return text;
}
